'use client';

import clsx from 'clsx';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {PropsWithChildren, ReactNode} from 'react';
import styles from './Snackbar.module.scss';

export type SnackbarVerticalPosition = 'top' | 'center' | 'bottom';
export type SnackbarHorizontalPosition = 'left' | 'center' | 'right';

export interface SnackbarProps extends PropsWithChildren {
  autoHideDuration?: number;
  horizontalPosition?: SnackbarHorizontalPosition;
  message?: string | ReactNode;
  onClose?: () => void;
  open?: boolean;
  verticalPosition?: SnackbarVerticalPosition;
}

const Snackbar = ({
  autoHideDuration = 5000,
  horizontalPosition = 'left',
  message = '',
  onClose,
  open = false,
  verticalPosition = 'bottom',
  ...props
}: SnackbarProps): ReactNode => {
  const [fadingOut, setFadingOut] = useState(false);
  const autoHideTimer = useRef<ReturnType<typeof setTimeout>>();

  const fadeOutAndClose = useCallback(() => {
    setFadingOut(true);
    setTimeout(() => {
      setFadingOut(false);
      onClose?.();
    }, 150);
  }, [onClose]);

  useEffect(() => {
    if (open && autoHideDuration) {
      autoHideTimer.current = setTimeout(
        fadeOutAndClose,
        autoHideDuration - 150,
      );
    }
    return () => clearTimeout(autoHideTimer.current);
  }, [open, autoHideDuration, fadeOutAndClose]);

  if (!open) {
    return <></>;
  }

  let xPositionCss = styles.left;
  if (horizontalPosition === 'center') {
    xPositionCss = styles.xCenter;
  } else if (horizontalPosition === 'right') {
    xPositionCss = styles.right;
  }

  let yPositionCss = styles.bottom;
  if (verticalPosition === 'center') {
    yPositionCss = styles.yCenter;
  } else if (verticalPosition === 'top') {
    yPositionCss = styles.top;
  }

  const snackbarCss = clsx(
    styles.container,
    styles.show,
    xPositionCss,
    yPositionCss,
    {
      [styles.fadingOut]: fadingOut,
    },
  );

  return (
    <section
      className={snackbarCss}
      {...props}
      role="presentation"
    >
      {!props.children && <span className={styles.message}>{message}</span>}
      {props.children && <div>{props.children}</div>}
    </section>
  );
};

export {Snackbar};
